import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi, checkoutApi } from '../services/api';
import ServiceAgreementPanel from '../components/ServiceAgreementPanel';
import {
  MENTEE_KEY_STORAGE,
  REGISTRATION_PATHWAY,
  REGISTRATION_PATHWAY_STORAGE,
  SCREENS,
} from '../lib/screenRegistry';
import { getMasterTestingKey } from '../lib/masterTestingKey';
import SubscriptionPlanSelector from '../components/SubscriptionPlanSelector';
import {
  DEFAULT_CLASSROOM_SUBSCRIPTION_PLAN_ID,
  resolveClassroomSubscriptionPlanKey,
} from '../lib/classroomSubscriptionPlans';
import { fireGtagEvent } from '../lib/gtag';
import './RegisterOrg.css';
import './RegisterPrivate.css';
import { ROUTES } from '../lib/classroomRoutes';

const DEFAULT_PLAN = DEFAULT_CLASSROOM_SUBSCRIPTION_PLAN_ID;

export default function RegisterPrivate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { masterSandboxUnlocked, refresh } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postcode, setPostcode] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [plan, setPlan] = useState(DEFAULT_PLAN);
  const [cardholderNameDifferent, setCardholderNameDifferent] = useState(false);
  const [billingAddressDifferent, setBillingAddressDifferent] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingPostcode, setBillingPostcode] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [copyNotice, setCopyNotice] = useState('');

  useEffect(() => {
    if (!accessKey) return;
    fireGtagEvent('learning_program_enrolled', accessKey);
  }, [accessKey]);

  useEffect(() => {
    const html = document.documentElement;
    const root = document.getElementById('root');
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: document.body.style.overflow,
      rootOverflow: root?.style.overflow ?? '',
    };

    html.style.overflowY = 'auto';
    document.body.style.overflowY = 'auto';
    if (root) root.style.overflowY = 'auto';

    return () => {
      html.style.overflow = prev.htmlOverflow;
      document.body.style.overflow = prev.bodyOverflow;
      if (root) root.style.overflow = prev.rootOverflow;
    };
  }, []);

  useEffect(() => {
    const checkoutStatus = searchParams.get('checkout');
    const sessionId = searchParams.get('session_id');
    if (checkoutStatus !== 'success' || !sessionId) return undefined;

    let cancelled = false;
    const poll = async () => {
      for (let attempt = 0; attempt < 12 && !cancelled; attempt += 1) {
        try {
          const result = await checkoutApi.getFulfillment(sessionId);
          if (result.subscriptionCode) {
            sessionStorage.setItem(MENTEE_KEY_STORAGE, result.subscriptionCode);
            sessionStorage.setItem(REGISTRATION_PATHWAY_STORAGE, REGISTRATION_PATHWAY.INDIVIDUAL);
            setAccessKey(result.subscriptionCode);
            return;
          }
        } catch {
          /* webhook may still be processing */
        }
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
      if (!cancelled) {
        setError('Payment received — your MPL-XXXX key is being issued. Refresh shortly or check your email.');
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [searchParams]);

  const contactAddressLine = [
    address.trim(),
    city.trim(),
    state.trim(),
    postcode.trim(),
  ].filter(Boolean).join(', ');

  const resolvedCardholderName = cardholderNameDifferent
    ? cardholderName.trim()
    : name.trim();

  const resolvedBillingAddress = billingAddressDifferent
    ? [
      billingAddress.trim(),
      billingCity.trim(),
      billingState.trim(),
      billingPostcode.trim(),
    ].filter(Boolean).join(', ')
    : contactAddressLine;

  const contactAddressValid = Boolean(
    address.trim()
    && city.trim()
    && state.trim()
    && postcode.trim(),
  );

  const cardholderNameValid = !cardholderNameDifferent || Boolean(cardholderName.trim());

  const billingAddressValid = !billingAddressDifferent || Boolean(
    billingAddress.trim()
    && billingCity.trim()
    && billingState.trim()
    && billingPostcode.trim(),
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed && !masterSandboxUnlocked) {
      setError('The mandatory Classroom Learning Agreement declaration must be accepted before registration.');
      return;
    }

    if (!contactAddressValid) {
      setError('Complete Address, City, State, and Postcode in registration details.');
      return;
    }

    if (!cardholderNameValid) {
      setError('Enter the credit card holder\'s name or uncheck the alternate cardholder option.');
      return;
    }

    if (!billingAddressValid) {
      setError('Complete all billing address fields or uncheck the alternate billing address option.');
      return;
    }

    setProcessing(true);
    setError('');
    try {
      if (masterSandboxUnlocked) {
        const result = await authApi.registerPrivate({
          name,
          email,
          plan,
          masterTestingKey: getMasterTestingKey(),
        });
        sessionStorage.setItem(MENTEE_KEY_STORAGE, result.userToken);
        sessionStorage.setItem(REGISTRATION_PATHWAY_STORAGE, REGISTRATION_PATHWAY.INDIVIDUAL);
        await refresh();
        setAccessKey(result.userToken);
        return;
      }

      const planKey = resolveClassroomSubscriptionPlanKey(plan);
      if (!planKey) {
        setError('Please select a pricing plan.');
        return;
      }

      if (resolvedCardholderName) {
        sessionStorage.setItem('mentology.cardholderName', resolvedCardholderName);
      } else {
        sessionStorage.removeItem('mentology.cardholderName');
      }

      if (resolvedBillingAddress) {
        sessionStorage.setItem('mentology.orgBillingAddress', resolvedBillingAddress);
      } else {
        sessionStorage.removeItem('mentology.orgBillingAddress');
      }

      const checkoutSession = await checkoutApi.createSession({
        planKey,
        customerEmail: email.trim(),
        customerName: name.trim(),
        address: resolvedBillingAddress || undefined,
        successPath: ROUTES.REGISTRATION_PRIVATE,
      });

      if (checkoutSession.url) {
        window.location.assign(checkoutSession.url);
        return;
      }

      setError('Unable to start secure checkout. Please try again.');
    } catch (err: unknown) {
      setError((err as { error?: string }).error || 'Registration failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleCopyAndContinue = async () => {
    if (!accessKey) return;
    try {
      await navigator.clipboard.writeText(accessKey);
      setCopyNotice('Key copied to clipboard.');
    } catch {
      setCopyNotice('Copy the key manually before continuing.');
    }
    setAccessKey('');
    navigate(ROUTES.LANDING, { replace: true });
  };

  const handleCopyKey = async () => {
    if (!accessKey) return;
    try {
      await navigator.clipboard.writeText(accessKey);
      setCopyNotice('Key copied to clipboard.');
    } catch {
      setCopyNotice('Unable to copy automatically.');
    }
  };

  return (
    <div className="page-center register-private-screen" data-screen-id={SCREENS.MPL03}>
      <div className="modal-box register-private-panel">
        <h2 className="section-header">Direct Private Registration</h2>
        <form onSubmit={handleSubmit} className="register-private-form" noValidate>
          <p className="register-private-section-label">Registration Details</p>
          <input
            className="input-field"
            placeholder="Full Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <input
            className="input-field"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            className="input-field"
            placeholder="Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            required
          />
          <div className="register-private-address-grid">
            <input
              className="input-field"
              placeholder="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              required
            />
            <input
              className="input-field"
              placeholder="State"
              value={state}
              onChange={(e) => setState(e.target.value)}
              required
            />
            <input
              className="input-field"
              placeholder="Postcode"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              required
            />
          </div>
          <div className="register-private-agreement-wrap">
            <ServiceAgreementPanel agreed={agreed} onAgreedChange={setAgreed} variant="compact" />
          </div>
          <SubscriptionPlanSelector
            selectedPlanId={plan}
            onSelectPlan={setPlan}
            sectionLabel="System Pricing & Checkout"
          />
          {plan && (
          <div className="register-private-stripe-alignment">
            <p className="register-private-stripe-alignment-label">Credit card alignment parameters</p>
            <label className="register-private-stripe-billing-toggle">
              <input
                type="checkbox"
                checked={cardholderNameDifferent}
                onChange={(e) => setCardholderNameDifferent(e.target.checked)}
                disabled={processing}
              />
              <span>Credit Card Holder&apos;s Name is different to Contact Name</span>
            </label>
            {cardholderNameDifferent && (
              <input
                className="input-field register-private-nested-field"
                placeholder="Cardholder Name"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                required
              />
            )}
            <label className="register-private-stripe-billing-toggle">
              <input
                type="checkbox"
                checked={billingAddressDifferent}
                onChange={(e) => setBillingAddressDifferent(e.target.checked)}
                disabled={processing}
              />
              <span>Billing Address is different to address above</span>
            </label>
            {billingAddressDifferent && (
              <div className="register-private-billing-grid">
                <input
                  className="input-field"
                  placeholder="Address"
                  value={billingAddress}
                  onChange={(e) => setBillingAddress(e.target.value)}
                  required
                />
                <input
                  className="input-field"
                  placeholder="City"
                  value={billingCity}
                  onChange={(e) => setBillingCity(e.target.value)}
                  required
                />
                <input
                  className="input-field"
                  placeholder="State"
                  value={billingState}
                  onChange={(e) => setBillingState(e.target.value)}
                  required
                />
                <input
                  className="input-field"
                  placeholder="Postcode"
                  value={billingPostcode}
                  onChange={(e) => setBillingPostcode(e.target.value)}
                  required
                />
              </div>
            )}
          </div>
          )}
          {error && <p className="error-text">{error}</p>}
          <div className="register-org-secure-checkout">
            <button
              type="submit"
              className="btn-primary register-org-secure-checkout-btn"
              disabled={processing || (!agreed && !masterSandboxUnlocked)}
            >
              {processing ? (
                <span className="register-org-submit-processing">
                  <span className="register-org-spinner" aria-hidden="true" />
                  Redirecting to secure checkout...
                </span>
              ) : (
                'PROCEED TO SECURE CHECKOUT'
              )}
            </button>
          </div>
        </form>
      </div>

      {accessKey && (
        <div
          className="register-org-success-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pro-success-title"
        >
          <div className="modal-box copper-border register-org-success-modal">
            <h2 id="pro-success-title" className="section-header">Registration Successful!</h2>
            <div className="register-org-success-key-row">
              <p className="register-org-success-subheader scannable-bullet-text">
                Your Paid Subscription Key (MPL-XXXX): <strong>{accessKey}</strong>
              </p>
              <button
                type="button"
                className="register-org-copy-btn"
                onClick={handleCopyKey}
                title="Copy access key to clipboard"
                aria-label="Copy access key to clipboard"
              >
                ⧉
              </button>
            </div>
            {copyNotice && <p className="register-org-copy-notice scannable-bullet-text">{copyNotice}</p>}
            <div className="button-control-group">
              <button type="button" className="btn-primary" onClick={handleCopyAndContinue}>
                Copy and Continue to Landing Page
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
